// src/pages/TeamInvitations.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import { ModalConfirm } from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import {
    EnvelopeIcon,
    XMarkIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const TeamInvitations = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [invitations, setInvitations] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedInvitation, setSelectedInvitation] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionResult, setActionResult] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        companyId: ''
    });

    // Stats
    const [stats, setStats] = useState({
        pending: 0,
        accepted: 0,
        rejected: 0,
        expired: 0
    });

    useEffect(() => {
        loadCompanies();
    }, []);

    useEffect(() => {
        if (filters.companyId) {
            loadInvitations();
        } else {
            // Clear invitations when no company selected
            setInvitations([]);
            setStats({ pending: 0, accepted: 0, rejected: 0, expired: 0 });
        }
    }, [filters]);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 1000, offset: 0 });
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadInvitations = async () => {
        if (!filters.companyId) return;

        try {
            const filterVars = {
                companyId: filters.companyId,
                status: filters.status ? [filters.status] : undefined
            };
            const data = await executeQuery(queries.getAdminTeamInvitations(), filterVars);
            let invitationList = data?.adminTeamInvitations || [];

            setInvitations(invitationList);

            // Calculate stats
            const newStats = {
                pending: invitationList.filter(i => i.status === 'PENDING').length,
                accepted: invitationList.filter(i => i.status === 'ACCEPTED').length,
                rejected: invitationList.filter(i => i.status === 'REJECTED').length,
                expired: invitationList.filter(i => i.status === 'EXPIRED').length
            };
            setStats(newStats);
        } catch (err) {
            console.error('Failed to load invitations:', err);
            // If the query fails (field doesn't exist yet), show empty state
            if (err.message?.includes('Cannot query field') || err.message?.includes('adminTeamInvitations')) {
                setInvitations([]);
                setActionResult({
                    message: 'Team invitations query is not available on the backend yet.',
                    isError: true
                });
            }
        }
    };

    const showActionResultMessage = (message, isError = false) => {
        setActionResult({ message, isError });
        setTimeout(() => setActionResult(null), 5000);
    };

    const handleCancelInvitation = async () => {
        if (!selectedInvitation) return;

        setActionLoading(true);
        try {
            const result = await executeMutation(mutations.adminCancelInvitation, {
                invitationId: selectedInvitation.id
            });
            if (result.cancelInvitation?.success) {
                showActionResultMessage(`Invitation to ${selectedInvitation.inviteeEmail} cancelled`);
                setShowCancelModal(false);
                setSelectedInvitation(null);
                await loadInvitations();
            } else {
                showActionResultMessage(result.cancelInvitation?.error || 'Failed to cancel invitation', true);
            }
        } catch (err) {
            showActionResultMessage(err.message, true);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'PENDING': return 'warning';
            case 'ACCEPTED': return 'success';
            case 'REJECTED': return 'danger';
            case 'EXPIRED': return 'default';
            case 'CANCELLED': return 'default';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatRelativeDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(dateString);
    };

    const columns = [
        {
            key: 'company',
            title: 'Company',
            render: (_, invitation) => (
                <div className="flex items-center">
                    <div className="bg-primary p-2 mr-3">
                        <BuildingOfficeIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <div className="font-medium text-white">
                            {invitation.company?.name || 'Unknown Company'}
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: 'inviteeEmail',
            title: 'Invitee Email',
            sortable: true,
            render: (email) => (
                <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-white">{email}</span>
                </div>
            )
        },
        {
            key: 'type',
            title: 'Type',
            render: (type) => (
                <span className={`px-2 py-1 text-xs font-medium ${
 type === 'INVITE' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
 }`}>
                    {type === 'INVITE' ? 'Invitation' : 'Join Request'}
                </span>
            )
        },
        {
            key: 'status',
            title: 'Status',
            render: (status) => <StatusBadge status={status.toLowerCase()} variant={getStatusBadgeColor(status)} />
        },
        {
            key: 'role',
            title: 'Role',
            render: (role) => role ? (
                <span className={`px-2 py-1 text-xs font-medium ${
 role === 'ADMIN' ? 'bg-red-50 text-red-700' :
 role === 'MANAGER' ? 'bg-blue-50 text-blue-700' :
 'bg-gray-700 text-gray-300'
 }`}>
                    {role}
                </span>
            ) : '-'
        },
        {
            key: 'createdAt',
            title: 'Created',
            sortable: true,
            render: (createdAt) => (
                <div className="text-sm">
                    <div className="text-white">{formatRelativeDate(createdAt)}</div>
                </div>
            )
        },
        {
            key: 'expiresAt',
            title: 'Expires',
            render: (expiresAt) => {
                if (!expiresAt) return <span className="text-gray-400">-</span>;
                const isExpired = new Date(expiresAt) < new Date();
                return (
                    <div className={`text-sm ${isExpired ? 'text-red-700' : 'text-gray-300'}`}>
                        {formatDate(expiresAt)}
                        {isExpired && <span className="text-xs ml-1">(expired)</span>}
                    </div>
                );
            }
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, invitation) => (
                <div className="flex space-x-2">
                    {invitation.status === 'PENDING' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInvitation(invitation);
                                setShowCancelModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-red-700"
                            title="Cancel invitation"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Team Invitations</h1>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-50 mr-3">
                            <ClockIcon className="h-6 w-6 text-yellow-700" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-white">{stats.pending}</div>
                            <div className="text-sm text-gray-400">Pending</div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-50 mr-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-700" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-white">{stats.accepted}</div>
                            <div className="text-sm text-gray-400">Accepted</div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-red-50 mr-3">
                            <XCircleIcon className="h-6 w-6 text-red-700" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-white">{stats.rejected}</div>
                            <div className="text-sm text-gray-400">Rejected</div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-gray-700 mr-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-white">{stats.expired}</div>
                            <div className="text-sm text-gray-400">Expired</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Result */}
            {actionResult && (
                <div className={`${actionResult.isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border p-4`}>
                    <p className={actionResult.isError ? 'text-red-700' : 'text-green-700'}>
                        {actionResult.message}
                    </p>
                </div>
            )}

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Company <span className="text-red-700">*</span>
                        </label>
                        <select
                            value={filters.companyId}
                            onChange={(e) => setFilters(prev => ({ ...prev, companyId: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">Select a company...</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            disabled={!filters.companyId}
                        >
                            <option value="">All Statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="ACCEPTED">Accepted</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="EXPIRED">Expired</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => setFilters({ status: '', companyId: '' })}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Invitations Table */}
            {!filters.companyId ? (
                <div className="bg-gray-800 p-8 text-center">
                    <BuildingOfficeIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Select a company to view team invitations</p>
                    <p className="text-gray-500 text-sm mt-2">Use the company dropdown above to filter invitations</p>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={invitations}
                    loading={loading}
                />
            )}

            {/* Cancel Confirmation Modal */}
            <ModalConfirm
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false);
                    setSelectedInvitation(null);
                }}
                onConfirm={handleCancelInvitation}
                title="Cancel Invitation"
                message={`Are you sure you want to cancel the invitation to "${selectedInvitation?.inviteeEmail}"?`}
                confirmLabel="Cancel Invitation"
                type="danger"
                isLoading={actionLoading}
            />
        </div>
    );
};

export default TeamInvitations;
